import { useCallback, useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useAuth } from './useAuth'
import { RootState } from '../redux/store'
import { setSurveyIds } from '../redux/slices/surveySlice'
import { setInstitution } from '../redux/slices/institutionSlice'
import apiServices from '../libs/services'
import { getIsLoggingOut } from '../libs/api'
import { PRE_SURVEY_ID_SLUG } from '../pages/PreSurveyPage'
import { POST_SURVEY_ID_SLUG } from '../pages/PostSurveyPage'
import { Institution, UserProfile } from '../models/global.types'
import { Dispatch } from 'redux'

type PendingSurvey = 'presurvey' | 'postsurvey' | null

interface SurveyCheckResult {
  pendingSurvey: PendingSurvey
}

interface SurveyCheckParams {
  userProfile: UserProfile | null
  institution: Institution | null
  preSurveyId: string | null
  postSurveyId: string | null
  surveyIdsLoaded: boolean
  dispatch: Dispatch
}

// Shared logic for checking survey conditions
async function checkSurveyConditions (params: SurveyCheckParams): Promise<SurveyCheckResult> {
  const { userProfile, dispatch } = params
  let { institution, preSurveyId, postSurveyId, surveyIdsLoaded } = params

  // 1. Ensure institution is loaded
  if (institution === null) {
    const res = await apiServices.getAccount()
    institution = res?.data?.account
    if (institution !== null && institution !== undefined) {
      dispatch(setInstitution(institution))
    }
  }

  // If surveys not enabled, no pending survey
  if (institution?.displaySurvey !== true) {
    return { pendingSurvey: null }
  }

  // Admin users should never see surveys
  if (userProfile?.role?.slug === 'admin') {
    return { pendingSurvey: null }
  }

  // 2. Ensure survey IDs are loaded
  if (!surveyIdsLoaded) {
    const [preRes, postRes] = await Promise.all([
      apiServices.getSurveyByUserGeneratedId(PRE_SURVEY_ID_SLUG),
      apiServices.getSurveyByUserGeneratedId(POST_SURVEY_ID_SLUG)
    ])
    preSurveyId = preRes?.data?.id ?? null
    postSurveyId = postRes?.data?.id ?? null
    if (preSurveyId !== null && postSurveyId !== null) {
      dispatch(setSurveyIds({ preSurveyId, postSurveyId }))
    }
  }

  // 3. Check conditions
  const completedIds = userProfile?.user_profile?.completed_survey_ids ?? []
  const acceptedTerms = userProfile?.user_profile?.accepted_terms === true
  const certDownloaded = userProfile?.user_profile?.certificate_downloaded === true

  // Presurvey pending?
  if (acceptedTerms && preSurveyId !== null && !completedIds.includes(preSurveyId)) {
    return { pendingSurvey: 'presurvey' }
  }

  // Postsurvey pending?
  const preCompleted = preSurveyId !== null && completedIds.includes(preSurveyId)
  const postNotCompleted = postSurveyId !== null && !completedIds.includes(postSurveyId)
  if (preCompleted && certDownloaded && postNotCompleted) {
    return { pendingSurvey: 'postsurvey' }
  }

  return { pendingSurvey: null }
}

interface UseSurveyGuardResult {
  loading: boolean
  pendingSurvey: PendingSurvey
  checkPendingSurveyAndRedirect: () => Promise<void>
}

export function useSurveyGuard (options?: { skip?: boolean }): UseSurveyGuardResult {
  const skip = options?.skip ?? false
  const [loading, setLoading] = useState(!skip)
  const [pendingSurvey, setPendingSurvey] = useState<PendingSurvey>(null)

  const navigate = useNavigate()
  const { userProfile } = useAuth()
  const { institution } = useSelector((state: RootState) => state.institution)
  const { preSurveyId, postSurveyId, loaded: surveyIdsLoaded } = useSelector((state: RootState) => state.survey)
  const dispatch = useDispatch()

  useEffect(() => {
    if (skip) return

    // No point checking surveys for a logged-out user; prevents unauthenticated API calls during logout
    if (userProfile === null || getIsLoggingOut()) {
      setLoading(false)
      setPendingSurvey(null)
      return
    }

    const runCheck = async (): Promise<void> => {
      try {
        const result = await checkSurveyConditions({
          userProfile,
          institution,
          preSurveyId,
          postSurveyId,
          surveyIdsLoaded,
          dispatch
        })
        setPendingSurvey(result.pendingSurvey)
      } catch (err) {
        // Suppress expected errors during logout; only log real errors
        if (!getIsLoggingOut()) {
          console.error('useSurveyGuard error:', err)
        }
        setPendingSurvey(null)
      } finally {
        setLoading(false)
      }
    }

    void runCheck()
  }, [skip, userProfile, institution, preSurveyId, postSurveyId, surveyIdsLoaded, dispatch])

  // Check for pending surveys and navigate to appropriate page
  const checkPendingSurveyAndRedirect = useCallback(async (): Promise<void> => {
    try {
      const result = await checkSurveyConditions({
        userProfile,
        institution,
        preSurveyId,
        postSurveyId,
        surveyIdsLoaded,
        dispatch
      })

      if (result.pendingSurvey === 'presurvey') {
        navigate('/presurvey', { replace: true })
      } else if (result.pendingSurvey === 'postsurvey') {
        navigate('/postsurvey', { replace: true })
      } else {
        navigate('/user-dashboard', { replace: true })
      }
    } catch (err) {
      // Suppress expected errors during logout; only log real errors
      if (!getIsLoggingOut()) {
        console.error('checkPendingSurveyAndRedirect error:', err)
      }
      navigate('/user-dashboard', { replace: true })
    }
  }, [userProfile, institution, preSurveyId, postSurveyId, surveyIdsLoaded, dispatch, navigate])

  return { loading, pendingSurvey, checkPendingSurveyAndRedirect }
}
